import { current } from '@reduxjs/toolkit'
import { FORMAT, formatDateTime } from '../../../helpers/datetime'
import { getLanguage } from '../../../helpers/language'
import { AUDIT_STATUS, DIRECT, METHOD } from './constant'

export const handleFinancialStatementIds = (payload, tabName) => {
  if (!payload?.length) {
    return []
  }

  const totalIndicator = payload.map((item) => item[tabName])?.flat()
  return [...new Set(totalIndicator.map((item) => item.ratioCode))]
}

export const handleAuditStatuses = (payload) => {
  if (!payload?.length) {
    return undefined
  }

  return payload.map((col) => {
    return {
      year: col.realYear,
      isAudit: col.isAudit,
    }
  })
}

export const handleMethod = (payload) => {
  if (!payload?.length) {
    return undefined
  }

  return payload.map((col) => {
    return {
      year: col.realYear,
      isDirect: col.isDirect,
    }
  })
}

export const handleNewLegalRegulationData = (payload, newLegalRegulation) => {
  if (!payload?.length) {
    return []
  }

  const result = []

  for (let i = payload.length - 1; i >= 0; i--) {
    const item = payload[i]
    if (item.reportFormId === newLegalRegulation) {
      result.push(item)
    } else {
      break
    }
  }

  return result.reverse()
}

export const handleLegalRegulationData = (payload = []) => {
  if (!payload.length) {
    return []
  }

  const result = []
  let lastLegalRegulation = null

  for (let i = payload.length - 1; i >= 0; i--) {
    const item = payload[i]
    if (item.reportFormId === lastLegalRegulation) {
      result[result.length - 1].push(item)
    } else {
      lastLegalRegulation = item.reportFormId
      result.push([item])
    }
  }

  return result.map((data, index) => {
    const isLastData = index === result.length - 1

    const firstPreviousData = !isLastData ? result[index + 1][0] : {}

    const newData = data.reverse()
    const firstData = newData[0]
    const lastData = newData[newData.length - 1]

    return {
      data: newData,
      years: newData.map((item) => item.realYear),
      yearChange: !isLastData ? firstData.realYear : lastData.realYear,
      newReportId: firstData.reportFormId,
      oldReportId: firstPreviousData.reportFormId,
      isLastData,
    }
  })
}

export const handleLegalRegulationChangeYear = (
  payload,
  newLegalRegulationData,
) => {
  if (!payload?.length || payload.length === newLegalRegulationData.length) {
    return undefined
  }
  return newLegalRegulationData[0]?.realYear
}

export const handleTabData = (payload, groupColumns, ids, tabName) => {
  if (!payload?.length) {
    return {}
  }

  const newGroupColumns = groupColumns?.[0].slice(1) || []
  const initialData = {}
  const initialValue = {}

  return ids?.reduce((acc, id) => {
    const value = newGroupColumns.reduce((value, col) => {
      return {
        ...value,
        [col.colId]: payload
          ?.filter((item) => item?.realYear === col?.colId)?.[0]
          [tabName]?.filter((val) => val?.ratioCode === id)?.[0],
      }
    }, initialValue)
    return {
      ...acc,
      [id]: {
        childrenIds: findAllChildrenId(payload?.[0]?.[tabName], id),
        col1: payload?.[0]?.[tabName]?.filter(
          (val) => val?.ratioCode === id,
        )?.[0]?.ratioName,
        parentId: payload?.[0]?.[tabName]?.filter(
          (i) => i?.ratioCode === id,
        )?.[0]?.parentRatioCode,
        fieldStyle: payload?.[0]?.[tabName]?.filter(
          (i) => i?.ratioCode === id,
        )?.[0]?.fieldStyle,
        ...value,
      },
    }
  }, initialData)
}

export const handleFiscalYearChange = (payload) => {
  if (!payload) {
    return undefined
  }
  const newFiscalYearEnd = formatDateTime(
    payload[0]?.fiscalYearEnd,
    FORMAT.DAY_MONTH,
  )
  const newFiscalYearEndData = payload.filter(
    (data) =>
      formatDateTime(data?.fiscalYearEnd, FORMAT.DAY_MONTH) ===
      newFiscalYearEnd,
  )
  if (payload.length > newFiscalYearEndData.length) {
    return newFiscalYearEndData[newFiscalYearEndData.length - 1].realYear
  }
  return undefined
}

export const handleNewGroupColumns = (payload, reportFormList) => {
  const locale = getLanguage()
  const initTitle = [
    [
      {
        title: 'financialStatement.FISCAL_END_YEAR',
        formatData: (data) => formatDateTime(data, FORMAT.DATE, locale),
        titleName: 'fiscalYearEnd',
        isSticky: true,
        colIdName: 'realYear',
        colId: 'col1',
        rowName: 'fiscalYearEnd',
      },
    ],
    [
      {
        title: 'financialStatement.LEGAL_REGULATION',
        titleName: 'reportFormId',
        formatData: (data) => {
          return reportFormList?.filter((i) => i.reportFormId === data)[0]
            ?.reportFormCode
        },
        colIdName: 'realYear',
        colId: 'col1',
        rowName: 'legalRegulation',
      },
    ],
    [
      {
        title: 'financialStatement.AUDIT_STATUS',
        titleName: 'isAudit',
        formatData: (data) => {
          return data
            ? 'financialStatement.AUDITED'
            : 'financialStatement.UNAUDITED'
        },
        selectData: Object.values(AUDIT_STATUS),
        colIdName: 'realYear',
        colId: 'col1',
        rowName: 'auditStatus',
      },
    ],
    [
      {
        title: 'financialStatement.METHOD',
        titleName: 'isDirect',
        formatData: (data) => {
          return data
            ? 'financialStatement.DIRECT'
            : 'financialStatement.INDIRECT'
        },
        selectData: Object.values(METHOD),
        colIdName: 'realYear',
        colId: 'col1',
        rowName: 'method',
      },
    ],
  ]
  const newGroupColumns = initTitle.map((row) => {
    const item = row[0]
    return payload.map((dataItem) => {
      const dataTitle = dataItem[item.titleName]
      return {
        title: item.formatData ? item.formatData(dataTitle) : dataTitle,
        colId: dataItem[item.colIdName],
        year: dataItem[item.colIdName],
        selectData: item.selectData ? item.selectData : undefined,
        isSticky: item.isSticky,
        rowName: item.rowName,
      }
    })
  })

  const groupColumnData = initTitle.map((row, index) => {
    return [...row, ...newGroupColumns[index]]
  })

  return groupColumnData
}

const findAllChildrenId = (data, parentId) => {
  if (!parentId) {
    return []
  }

  const childrenIds = data
    ?.filter((id) => id.parentRatioCode === parentId)
    .map((item) => {
      const childOfChildIds = findAllChildrenId(data, item.ratioCode)
      return [item.ratioCode, ...childOfChildIds]
    })

  return childrenIds.flat(1)
}

export const handleChangeTabData = (
  data,
  ids,
  oldData,
  tabName,
  yearChange,
) => {
  const yearChanges = JSON.parse(JSON.stringify(current(yearChange)))
  const newData = JSON.parse(JSON.stringify(current(oldData)))
  ids.forEach((id) => {
    yearChanges.forEach((year) => {
      const yearData = data.find((dataItem) => dataItem.realYear === year)
      newData[id][year] = data.length
        ? yearData[tabName].find((item) => item.ratioCode === id)
        : undefined
    })
  })
  return newData
}

export const handleDataGetCurrency = (data) => {
  return data.reduce((prev, curr) => {
    return [
      ...prev,
      ...[
        ...curr.balanceSheets,
        ...curr.cashFlows,
        ...curr.incomeStatements,
      ].map((item) => ({
        value: item.value,
        year: curr.realYear,
        exchangeRateType: item.exchangeRateType,
      })),
    ]
  }, [])
}

export const handleYearChangeFiscalYear = (fiscalYearData) => {
  const innerProcess = (data) => {
    const newFiscalYear = data[0]
    const yearChange = data.find(
      (item) => item.fiscalYearEnd !== newFiscalYear.fiscalYearEnd,
    )
    const indexOfYearChange = data.indexOf(yearChange)
    if (indexOfYearChange > 0) {
      return [yearChange, innerProcess(data.slice(indexOfYearChange))].flat()
    }
    return yearChange
  }
  return innerProcess(fiscalYearData)?.filter((item) => item)
}

export const handleReportFormId = (reportFormIdData) => {
  const innerProcess = (data) => {
    const newReportFormId = data[0]
    const yearChange = data.find(
      (item) => item.reportFormId !== newReportFormId.reportFormId,
    )
    const indexOfYearChange = data.indexOf(yearChange)
    if (indexOfYearChange > 0) {
      return [yearChange, innerProcess(data.slice(indexOfYearChange))].flat()
    }
    return yearChange
  }
  return innerProcess(reportFormIdData)?.filter((item) => item)
}

export const mappingDataApi = (data) => {
  return data.map((itemYear) => {
    const cashFlows = itemYear.cashFlows

    return {
      ...itemYear,
      cashFlows: cashFlows.map((indicator) => ({
        ...indicator,
        ratioCode: indicator.isDirect
          ? indicator.ratioCode + DIRECT
          : indicator.ratioCode,
        parentRatioCode: indicator.parentRatioCode
          ? indicator.isDirect
            ? indicator.parentRatioCode + DIRECT
            : indicator.parentRatioCode
          : undefined,
      })),
    }
  })
}
